import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Eye, EyeOff, LogOut, Bell, Download, X, Smartphone, Monitor, Share } from 'lucide-react';
import { formatCurrency, getCurrencySymbol } from '../utils/currency';

const API = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
  ? 'http://localhost:8001/api' 
  : '/api';

function InvestmentDashboard({ user, setUser }) {
  const navigate = useNavigate();
  const [balance, setBalance] = useState(0);
  const [showBalance, setShowBalance] = useState(true);
  const [trendingStocks, setTrendingStocks] = useState([]);
  const [favoriteStocks, setFavoriteStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewsMenu, setShowNewsMenu] = useState(false);
  const [brokerLogo, setBrokerLogo] = useState(null);
  const [brokerName, setBrokerName] = useState('');
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [showInstallModal, setShowInstallModal] = useState(false);

  // Platform tespiti
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isAndroid = /Android/.test(navigator.userAgent);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isChrome = /Chrome/.test(navigator.userAgent) && !/Edge/.test(navigator.userAgent);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

  useEffect(() => {
    fetchUserData();
    fetchTrendingStocks();
    fetchFavoriteStocks();
    fetchBrokerInfo();
  }, []);

  // Fetch unread notifications count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await fetch(`${API}/notifications/unread/count`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        setUnreadNotifications(data.count || 0);
      } catch (err) {
        console.error('Failed to fetch notification count:', err);
      }
    };
    
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // Poll every 30 seconds (performans)
    
    return () => clearInterval(interval);
  }, []);

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const userData = await response.json();
        setBalance(userData.balance || 0);
      }
    } catch (err) {
      console.error('Failed to fetch user data:', err);
    }
  };

  const fetchTrendingStocks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/assets`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const assets = await response.json();
        const sorted = assets
          // Sadece Borsa İstanbul hisselerini filtrele
          .filter(a => a.category === 'BIST_100' || a.category === 'BORSA_ISTANBUL' || a.category === 'BIST')
          .map(a => {
            // Günlük değişim: opening_price (açılış) ile current_price arasındaki fark
            let pct = 0;
            const current = a.current_price;
            const opening = a.opening_price || a.closing_price;
            
            if (current && opening && opening > 0) {
              pct = ((current - opening) / opening) * 100;
            }
            return { ...a, change_percent: pct };
          })
          .filter(a => {
            // Filter: Borsa kuralı - BIST'te günlük max %10 değişim
            // Sadece pozitif ve gerçekçi değerleri göster
            const pct = a.change_percent;
            return pct > 0 && pct <= 10;
          })
          .sort((a, b) => (b.change_percent || 0) - (a.change_percent || 0))
          .slice(0, 5);
        setTrendingStocks(sorted);
      }
    } catch (err) {
      console.error('Failed to fetch trending stocks:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFavoriteStocks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/users/favorites`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const favorites = await response.json();
        setFavoriteStocks(favorites);
      }
    } catch (err) {
      console.error('Failed to fetch favorites:', err);
    }
  };

  const fetchBrokerInfo = async () => {
    try {
      const response = await fetch(`${API}/homepage-broker`);
      if (response.ok) {
        const broker = await response.json();
        if (broker && broker.logo_url) {
          const logoUrl = broker.logo_url.startsWith('http') 
            ? broker.logo_url 
            : `${API.replace('/api', '')}${broker.logo_url}`;
          setBrokerLogo(logoUrl);
          setBrokerName(broker.name);
        }
      }
    } catch (err) {
      console.error('Failed to fetch broker:', err);
    }
  };

  const handleStockClick = (stock) => {
    navigate(`/trading?asset=${stock.id}`);
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <div className="bg-gray-900 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {brokerLogo ? (
                <img src={brokerLogo} alt={brokerName} className="h-12 w-auto object-contain" />
              ) : (
                <h1 className="text-white text-xl font-bold">Yatırım</h1>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* Uygulamayı Yükle Butonu - Standalone modda değilse göster */}
              {!isStandalone && (
                <button
                  onClick={() => setShowInstallModal(true)}
                  className="flex items-center gap-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-2 py-1.5 rounded-lg text-xs font-medium transition-all"
                  title="Uygulamayı Yükle"
                >
                  <Download size={14} />
                  <span>Uygulamayı Yükle</span>
                </button>
              )}
              {/* Bildirim İkonu */}
              <button
                onClick={() => navigate('/notifications')}
                className="relative text-gray-400 hover:text-white transition-colors p-2"
                title="Bildirimler"
              >
                <Bell size={20} className={unreadNotifications > 0 ? 'text-yellow-400 animate-bounce' : ''} />
                {unreadNotifications > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center border-2 border-gray-900">
                    {unreadNotifications > 9 ? '9+' : unreadNotifications}
                  </span>
                )}
              </button>
              <button
                onClick={() => setShowBalance(!showBalance)}
                className="text-gray-400 hover:text-white transition-colors p-2"
                title={showBalance ? "Bakiyeyi Gizle" : "Bakiyeyi Göster"}
              >
                {showBalance ? <Eye size={20} /> : <EyeOff size={20} />}
              </button>
              <button
                onClick={() => {
                  localStorage.removeItem('token');
                  localStorage.removeItem('user');
                  window.location.href = '/';
                }}
                className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg transition-colors"
                title="Çıkış Yap"
              >
                <LogOut size={16} />
                <span className="text-xs font-medium hidden sm:inline">Çıkış</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6 page-content-with-nav">
        {/* Balance Section */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
          <div className="text-center mb-6">
            <p className="text-gray-400 text-sm mb-2">Toplam Varlık</p>
            <p className="text-white text-4xl font-bold mb-1">
              {showBalance ? formatCurrency(balance + (user?.credit || 0), user) : `${getCurrencySymbol(user)}•••••`}
            </p>
            <p className="text-gray-400 text-sm">{getCurrencySymbol(user)}0,00 (%0,00) Günlük</p>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => navigate('/deposit')}
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white py-3 rounded-lg font-medium transition-all"
            >
              Yatır
            </button>
            <button
              onClick={() => navigate('/withdraw')}
              className="bg-gray-700 hover:bg-gray-600 text-white py-3 rounded-lg font-medium transition-all"
            >
              Çek
            </button>
          </div>
          
          {/* PWA Install Button kaldırıldı */}
        </div>

        {/* Currency Holdings */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-2">
              <span className="text-3xl">{user?.account_type === 'VIP' ? '🇺🇸' : '🇹🇷'}</span>
              <span className="text-white font-medium">{user?.account_type === 'VIP' ? 'USD' : 'TL'}</span>
            </div>
            <span className="text-white font-medium">
              {showBalance ? formatCurrency(balance, user) : `${getCurrencySymbol(user)}•••••`}
            </span>
          </div>
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-2">
              <span className="text-3xl">🇺🇸</span>
              <span className="text-white font-medium">USD</span>
            </div>
            <span className="text-white font-medium">
              {showBalance ? '$0,00' : '$•••••'}
            </span>
          </div>
        </div>

        {/* Lists Section */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
          <h2 className="text-white text-lg font-bold mb-4">Listeler</h2>

          {/* Trending Stocks */}
          <div className="mb-6">
            <h3 className="text-white text-base font-medium mb-3 flex items-center gap-2">
              <TrendingUp size={18} className="text-green-400" />
              Trend Hisseler
            </h3>
            {loading ? (
              <p className="text-gray-400 text-sm">Yükleniyor...</p>
            ) : trendingStocks.length > 0 ? (
              <div className="space-y-2">
                {trendingStocks.map((stock) => (
                  <button
                    key={stock.id}
                    onClick={() => handleStockClick(stock)}
                    className="w-full bg-gray-700 hover:bg-gray-600 rounded-lg p-3 flex items-center justify-between transition-all"
                  >
                    <div className="text-left">
                      <p className="text-white font-medium text-sm">{stock.symbol}</p>
                      <p className="text-gray-400 text-xs">{stock.name || 'Hisse'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-medium text-sm">{getCurrencySymbol(user)}{stock.current_price?.toFixed(2)}</p>
                      <p className="text-green-400 text-xs font-medium">
                        +{stock.change_percent?.toFixed(2)}%
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-sm">Henüz trend hisse yok</p>
            )}
          </div>

          {/* Favorite Stocks */}
          <div>
            <h3 className="text-white text-base font-medium mb-3">Favori Hisseler</h3>
            {favoriteStocks.length > 0 ? (
              <div className="space-y-2">
                {favoriteStocks.map((stock) => (
                  <button
                    key={stock.id}
                    onClick={() => handleStockClick(stock)}
                    className="w-full bg-gray-700 hover:bg-gray-600 rounded-lg p-3 flex items-center justify-between transition-all"
                  >
                    <div className="text-left">
                      <p className="text-white font-medium text-sm">{stock.symbol}</p>
                      <p className="text-gray-400 text-xs">{stock.name || 'Hisse'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-medium text-sm">{getCurrencySymbol(user)}{stock.current_price?.toFixed(2)}</p>
                      <p className={`text-xs font-medium ${stock.change_percent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {stock.change_percent >= 0 ? '+' : ''}{stock.change_percent?.toFixed(2)}%
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-sm">Henüz favori hisse eklenmedi</p>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Navigation - Global component App.js'de */}

      {/* Ana Ekrana Ekle Tarif Modalı */}
      {showInstallModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70" onClick={() => setShowInstallModal(false)}>
          <div 
            className="bg-gray-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-sm max-h-[85vh] overflow-hidden shadow-2xl sm:m-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                {brokerLogo && (
                  <img src={brokerLogo} alt={brokerName} className="w-8 h-8 rounded-lg bg-white p-0.5 object-contain" />
                )}
                <div>
                  <h3 className="text-white font-bold text-base">{brokerName || 'Uygulamayı Yükle'}</h3>
                  <p className="text-white/80 text-xs">Ana Ekrana Ekle</p>
                </div>
              </div>
              <button
                onClick={() => setShowInstallModal(false)}
                className="text-white/80 hover:text-white p-1 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Content - Scrollable */}
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {/* Bilgi */}
              <div className="bg-gray-700/50 rounded-lg p-3 mb-4">
                <p className="text-gray-300 text-xs text-center">
                  <span className="text-white font-semibold">{brokerName}</span> uygulamasını ana ekranına ekle
                </p>
              </div>

              {/* iOS Safari Talimatları */}
              {isIOS && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-blue-400 font-medium text-sm mb-2">
                    <Smartphone size={16} />
                    <span>iPhone / iPad</span>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 bg-gray-700/30 rounded-lg p-2">
                      <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0">1</div>
                      <p className="text-white text-sm">Alttaki <Share size={12} className="inline mx-1" /> <span className="font-medium">Paylaş</span> butonuna dokun</p>
                    </div>
                    
                    <div className="flex items-center gap-2 bg-gray-700/30 rounded-lg p-2">
                      <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0">2</div>
                      <p className="text-white text-sm"><span className="font-medium">Ana Ekrana Ekle</span>'yi bul ve dokun</p>
                    </div>
                    
                    <div className="flex items-center gap-2 bg-gray-700/30 rounded-lg p-2">
                      <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0">3</div>
                      <p className="text-white text-sm">Sağ üstteki <span className="font-medium">Ekle</span>'ye dokun</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Android Chrome Talimatları */}
              {isAndroid && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-green-400 font-medium text-sm mb-2">
                    <Smartphone size={16} />
                    <span>Android</span>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 bg-gray-700/30 rounded-lg p-2">
                      <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0">1</div>
                      <p className="text-white text-sm">Sağ üstteki <span className="font-bold">⋮</span> menüye dokun</p>
                    </div>
                    
                    <div className="flex items-center gap-2 bg-gray-700/30 rounded-lg p-2">
                      <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0">2</div>
                      <p className="text-white text-sm"><span className="font-medium">Ana ekrana ekle</span>'yi seç</p>
                    </div>
                    
                    <div className="flex items-center gap-2 bg-gray-700/30 rounded-lg p-2">
                      <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0">3</div>
                      <p className="text-white text-sm"><span className="font-medium">Yükle</span>'ye dokun</p>
                    </div>
                  </div>
                </div>
              )}

              {/* PC/Desktop Talimatları */}
              {!isIOS && !isAndroid && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-purple-400 font-medium text-sm mb-2">
                    <Monitor size={16} />
                    <span>Bilgisayar</span>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 bg-gray-700/30 rounded-lg p-2">
                      <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0">1</div>
                      <p className="text-white text-sm">Adres çubuğunun sağına bak</p>
                    </div>
                    
                    <div className="flex items-center gap-2 bg-gray-700/30 rounded-lg p-2">
                      <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0">2</div>
                      <p className="text-white text-sm"><span className="font-bold">⊕</span> veya <Download size={12} className="inline" /> simgesine tıkla</p>
                    </div>
                    
                    <div className="flex items-center gap-2 bg-gray-700/30 rounded-lg p-2">
                      <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0">3</div>
                      <p className="text-white text-sm"><span className="font-medium">Yükle</span>'ye tıkla</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Alt Bilgi */}
              <div className="mt-3 pt-3 border-t border-gray-700">
                <p className="text-gray-400 text-xs text-center">
                  ✨ <span className="text-white font-medium">{brokerName}</span> adı ve logosu ile yüklenecek
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-3 bg-gray-900/50">
              <button
                onClick={() => setShowInstallModal(false)}
                className="w-full bg-gray-700 hover:bg-gray-600 text-white py-2.5 rounded-xl font-medium transition-colors text-sm"
              >
                Anladım
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InvestmentDashboard;
